import { useState, useEffect } from "react";
import api from "../api";
import type { GuardianKpi, GuardianRunRecord, CoverageThresholds, SecurityFinding } from "../api";
import SecurityFindings from "../components/SecurityFindings";

// ─── Guardian Dashboard ─────────────────────────────────────────
// Task 7: KPI cards, coverage trend, flaky tests, run history
// + Security Findings tab (Phase 9)

type GuardianTab = 'kpis' | 'security';

interface Props {
  projectId: string;
}

function pct(v: string | null): string {
  if (!v) return "—";
  const n = parseFloat(v);
  return isNaN(n) ? "—" : `${n.toFixed(1)}%`;
}

function delta(v: string | null): { text: string; color: string } {
  if (!v) return { text: "", color: "" };
  const n = parseFloat(v);
  if (isNaN(n)) return { text: "", color: "" };
  const sign = n >= 0 ? "+" : "";
  const color = n > 0 ? "var(--green)" : n < 0 ? "var(--red)" : "var(--text-muted)";
  return { text: `${sign}${n.toFixed(1)}%`, color };
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Guardian({ projectId }: Props) {
  const [tab, setTab] = useState<GuardianTab>('kpis');
  const [kpis, setKpis] = useState<GuardianKpi[]>([]);
  const [latest, setLatest] = useState<GuardianKpi | null>(null);
  const [runs, setRuns] = useState<GuardianRunRecord[]>([]);
  const [thresholds, setThresholds] = useState<CoverageThresholds | null>(null);
  const [securityFindings, setSecurityFindings] = useState<SecurityFinding[]>([]);
  const [securityBaselineAt, setSecurityBaselineAt] = useState<string | null>(null);
  const [securityScore, setSecurityScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      (api as any).guardianKpis.list(projectId, 20).catch(() => []),
      (api as any).guardianKpis.latest(projectId).catch(() => null),
      (api as any).guardianKpis.runs(projectId, 20).catch(() => []),
      (api as any).guardianKpis.thresholds(projectId).catch(() => ({ line: 80, branch: 60, function: 80 })),
      api.getProject(projectId).catch(() => null),
    ]).then(([kpiList, latestKpi, runList, thresh, project]) => {
      setKpis(kpiList);
      setLatest(latestKpi);
      setRuns(runList);
      setThresholds(thresh);
      if (project) {
        setSecurityFindings(project.security_findings ?? []);
        setSecurityBaselineAt(project.security_baseline_at ?? null);
        setSecurityScore(project.security_score ?? null);
      }
    }).catch(err => {
      setError(err instanceof Error ? err.message : "Failed to load Guardian data");
    }).finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
        Loading Guardian KPIs...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "var(--red)" }}>
        {error}
      </div>
    );
  }

  const lineDelta = delta(latest?.line_coverage_delta ?? null);
  const timeDeltaVal = latest?.execution_time_delta ? delta(latest.execution_time_delta) : { text: "", color: "" };

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>🛡️</span> Guardian Dashboard
        </h2>
        <div style={{ display: "flex", gap: 4, background: "var(--bg-card)", borderRadius: 8, padding: 3, border: "1px solid var(--border)" }}>
          <TabButton active={tab === 'kpis'} onClick={() => setTab('kpis')}>KPIs</TabButton>
          <TabButton active={tab === 'security'} onClick={() => setTab('security')}>
            Security {securityFindings.length > 0 && <span style={{ fontSize: 10, background: "rgba(239,68,68,0.15)", color: "#ef4444", borderRadius: 10, padding: "1px 6px", marginLeft: 4 }}>{securityFindings.length}</span>}
          </TabButton>
        </div>
      </div>

      {tab === 'security' && (
        <SecurityFindings findings={securityFindings} baselineAt={securityBaselineAt} score={securityScore} />
      )}

      {tab === 'kpis' && (<>
      {/* Original KPI content below */}

      {/* ─── KPI Cards ─────────────────────────────────── */}
      {latest ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
          <KpiCard
            label="Line Coverage"
            value={pct(latest.line_coverage)}
            delta={lineDelta}
            threshold={thresholds ? `${thresholds.line}%` : undefined}
            warn={latest.line_coverage && thresholds ? parseFloat(latest.line_coverage) < thresholds.line : false}
          />
          <KpiCard
            label="Branch Coverage"
            value={pct(latest.branch_coverage)}
            threshold={thresholds ? `${thresholds.branch}%` : undefined}
            warn={latest.branch_coverage && thresholds ? parseFloat(latest.branch_coverage) < thresholds.branch : false}
          />
          <KpiCard
            label="Function Coverage"
            value={pct(latest.function_coverage)}
            threshold={thresholds ? `${thresholds.function}%` : undefined}
            warn={latest.function_coverage && thresholds ? parseFloat(latest.function_coverage) < thresholds.function : false}
          />
          <KpiCard
            label="Pass Rate"
            value={pct(latest.pass_rate)}
            warn={latest.pass_rate ? parseFloat(latest.pass_rate) < 100 : false}
          />
          <KpiCard
            label="Total Tests"
            value={latest.total_tests?.toString() ?? "—"}
            subtitle={[
              latest.tests_unit ? `${latest.tests_unit} unit` : null,
              latest.tests_integration ? `${latest.tests_integration} int` : null,
              latest.tests_e2e ? `${latest.tests_e2e} e2e` : null,
            ].filter(Boolean).join(" · ") || undefined}
          />
          <KpiCard
            label="Flaky Tests"
            value={latest.flaky_test_count?.toString() ?? "0"}
            warn={(latest.flaky_test_count ?? 0) > 0}
          />
          <KpiCard
            label="Execution Time"
            value={latest.execution_time_ms ? `${(latest.execution_time_ms / 1000).toFixed(1)}s` : "—"}
            delta={timeDeltaVal}
          />
          <KpiCard
            label="Regression Rate"
            value={pct(latest.regression_rate)}
            warn={latest.regression_rate ? parseFloat(latest.regression_rate) > 0 : false}
          />
        </div>
      ) : (
        <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", background: "var(--bg-card)", borderRadius: 8, marginBottom: "2rem" }}>
          No KPI data yet. Run Guardian to generate the first snapshot.
        </div>
      )}

      {/* ─── Coverage Trend (simple ASCII-style bar chart) ─── */}
      {kpis.length > 1 && (
        <div style={{ marginBottom: "2rem" }}>
          <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, marginBottom: "0.75rem" }}>Coverage Trend</h3>
          <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: "1rem", overflowX: "auto" }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 120, minWidth: kpis.length * 40 }}>
              {[...kpis].reverse().map((kpi, i) => {
                const cov = kpi.line_coverage ? parseFloat(kpi.line_coverage) : 0;
                const h = Math.max(4, (cov / 100) * 100);
                const belowThreshold = thresholds && cov < thresholds.line;
                return (
                  <div key={kpi.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, minWidth: 30 }}>
                    <span style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 2 }}>{cov.toFixed(0)}%</span>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 28,
                        height: h,
                        background: belowThreshold ? "var(--red)" : "var(--green)",
                        borderRadius: "4px 4px 0 0",
                        opacity: 0.8,
                      }}
                      title={`${new Date(kpi.created_at).toLocaleDateString()} — ${cov.toFixed(1)}%`}
                    />
                    <span style={{ fontSize: 9, color: "var(--text-muted)", marginTop: 2 }}>
                      {new Date(kpi.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                );
              })}
            </div>
            {thresholds && (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
                Threshold: {thresholds.line}% line · {thresholds.branch}% branch · {thresholds.function}% function
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Flaky Tests Table ─────────────────────────── */}
      {latest?.flaky_tests && latest.flaky_tests.length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, marginBottom: "0.75rem" }}>
            Flaky Tests ({latest.flaky_tests.length})
          </h3>
          <div style={{ background: "var(--bg-card)", borderRadius: 8, overflow: "hidden" }}>
            <table className="ocn-grid">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th className="nowrap" style={{ textAlign: "left", padding: "0.5rem 1rem", fontWeight: 500 }}>Test Name</th>
                  <th className="nowrap" style={{ textAlign: "center", padding: "0.5rem 1rem", fontWeight: 500 }}>Fail Count</th>
                  <th className="nowrap" style={{ textAlign: "right", padding: "0.5rem 1rem", fontWeight: 500 }}>Last Failed</th>
                </tr>
              </thead>
              <tbody>
                {latest.flaky_tests.map((ft, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "0.5rem 1rem", fontFamily: "var(--font-mono, monospace)" }}>{ft.test_name}</td>
                    <td className="num" style={{ padding: "0.5rem 1rem", textAlign: "center", color: "var(--red)" }}>{ft.fail_count}</td>
                    <td className="nowrap" style={{ padding: "0.5rem 1rem", textAlign: "right", color: "var(--text-muted)" }}>{timeAgo(ft.last_failed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Run History ───────────────────────────────── */}
      <div>
        <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, marginBottom: "0.75rem" }}>
          Guardian Run History ({runs.length})
        </h3>
        {runs.length === 0 ? (
          <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", background: "var(--bg-card)", borderRadius: 8 }}>
            No Guardian runs yet.
          </div>
        ) : (
          <div style={{ background: "var(--bg-card)", borderRadius: 8, overflow: "hidden" }}>
            <table className="ocn-grid">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th className="nowrap" style={{ textAlign: "left", padding: "0.5rem 1rem", fontWeight: 500 }}>When</th>
                  <th className="nowrap" style={{ textAlign: "left", padding: "0.5rem 1rem", fontWeight: 500 }}>Trigger</th>
                  <th className="nowrap" style={{ textAlign: "center", padding: "0.5rem 1rem", fontWeight: 500 }}>Status</th>
                  <th className="nowrap" style={{ textAlign: "center", padding: "0.5rem 1rem", fontWeight: 500 }}>Regressions</th>
                  <th className="nowrap" style={{ textAlign: "center", padding: "0.5rem 1rem", fontWeight: 500 }}>Fixed</th>
                  <th className="nowrap" style={{ textAlign: "right", padding: "0.5rem 1rem", fontWeight: 500 }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const regCount = Array.isArray(run.regressions_json) ? run.regressions_json.length : 0;
                  const fixedCount = Array.isArray(run.fixed_json) ? run.fixed_json.length : 0;
                  const statusColor =
                    run.status === "completed" && regCount === 0 ? "var(--green)" :
                    run.status === "completed" && regCount > 0 ? "var(--yellow, #eab308)" :
                    run.status === "failed" ? "var(--red)" :
                    "var(--text-muted)";
                  return (
                    <tr key={run.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td className="nowrap" style={{ padding: "0.5rem 1rem" }}>
                        {run.completed_at ? timeAgo(run.completed_at) : run.started_at ? timeAgo(run.started_at) : "—"}
                      </td>
                      <td style={{ padding: "0.5rem 1rem" }}>{run.trigger}</td>
                      <td className="nowrap" style={{ padding: "0.5rem 1rem", textAlign: "center" }}>
                        <span style={{ color: statusColor, fontWeight: 500 }}>{run.status}</span>
                      </td>
                      <td className="num" style={{ padding: "0.5rem 1rem", textAlign: "center", color: regCount > 0 ? "var(--red)" : "var(--green)" }}>
                        {regCount}
                      </td>
                      <td className="num" style={{ padding: "0.5rem 1rem", textAlign: "center", color: fixedCount > 0 ? "var(--green)" : "var(--text-muted)" }}>
                        {fixedCount}
                      </td>
                      <td className="num" style={{ padding: "0.5rem 1rem", textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>
                        ${run.cost_usd.toFixed(3)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}

// ─── Tab Button Component ─────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "0.35rem 0.75rem",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        fontSize: "var(--text-sm)",
        fontWeight: active ? 600 : 400,
        background: active ? "var(--bg-primary, rgba(255,255,255,0.1))" : "transparent",
        color: active ? "var(--text-primary, inherit)" : "var(--text-muted)",
        transition: "all 0.15s",
        display: "flex",
        alignItems: "center",
      }}
    >
      {children}
    </button>
  );
}

// ─── KPI Card Component ─────────────────────────────────────────

function KpiCard({ label, value, delta: d, threshold, subtitle, warn }: {
  label: string;
  value: string;
  delta?: { text: string; color: string };
  threshold?: string;
  subtitle?: string;
  warn?: boolean;
}) {
  return (
    <div style={{
      background: "var(--bg-card)",
      borderRadius: 8,
      padding: "1rem",
      border: warn ? "1px solid var(--red)" : "1px solid var(--border)",
    }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </div>
      <div style={{ fontSize: "var(--text-xl, 1.25rem)", fontWeight: 700, display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ color: warn ? "var(--red)" : "var(--text-primary, inherit)" }}>{value}</span>
        {d?.text && (
          <span style={{ fontSize: "var(--text-xs)", color: d.color, fontWeight: 500 }}>{d.text}</span>
        )}
      </div>
      {threshold && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>min: {threshold}</div>
      )}
      {subtitle && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{subtitle}</div>
      )}
    </div>
  );
}
